// src/components/map/MiniMap.tsx
//
// Inline mini-map for Discover cards and detail sheets. Renders a
// destination pin, the user's live location dot, and a real walking
// polyline that follows streets via Google Directions API.
//
// RENDER STRATEGY (depth-aware, for use inside swipe deck):
//   - depth 0 (top card visible to user): full live <Map>, walking
//     route fetched + drawn. This is the only card that mounts a
//     real Maps SDK instance.
//   - depth 1 (next up): Static Maps API tile (one image fetch, ~5KB).
//     Pre-fetched so promotion to depth 0 feels instant.
//   - depth 2+: lightweight gradient placeholder, no image fetch.
//
// FALLBACK CHAIN:
//   1. Live Google Map (best)
//   2. Static Maps tile if SDK fails to mount
//   3. Gradient placeholder if Static fails too
//
// PERFORMANCE NOTES:
// - Single shared <APIProvider> at app root means the SDK loads once,
//   not per-card.
// - Walking routes cache in-memory keyed by destination + user coords
//   rounded to 4 decimals (~11m precision).
// - User dot updates as useLocation updates; map auto-refits bounds
//   only when destination changes, not when user dot wiggles, so the
//   view doesn't constantly re-zoom.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  Map,
  AdvancedMarker,
  useMap,
  useApiIsLoaded,
} from '@vis.gl/react-google-maps';
import { useLocation } from '../../hooks/useLocation';
import { useWalkingRoute } from '../../hooks/useWalkingRoute';
import { UserLocationMarker } from './UserLocationMarker';
import { useT } from '../../i18n/useT';

interface Destination {
  lat: number;
  lng: number;
  name?: string;
}

interface MiniMapProps {
  destination: Destination;
  /** When true, mounts the live Maps SDK. Otherwise renders static
   *  thumbnail or placeholder. Pass `depth === 0` here. */
  active?: boolean;
  /** Stack depth for choosing between static thumb (1) and gradient (2+). */
  depth?: number;
  /** Pixel height. */
  height?: number | string;
  /** Pixel aspect ratio if you want responsive width-driven sizing. */
  aspectRatio?: string;
  /** Enable user gesture interaction (pan, zoom). Off inside swipe
   *  deck so map gestures don't conflict with card swipes. */
  interactive?: boolean;
}

/** Distance threshold beyond which we drop the polyline and just frame
 *  user + destination together. >3 km isn't a meaningful walk. */
const WALK_LIMIT_M = 3000;

const STATIC_MAPS_BASE = 'https://maps.googleapis.com/maps/api/staticmap';

const STATIC_KEY: string =
  (import.meta as any).env?.VITE_GOOGLE_MAPS_BROWSER_KEY || '';

/** Build a one-shot Static Maps URL for a given destination + user.
 *  Cheap, cached by Google's CDN, returns ~5KB PNG. */
function buildStaticUrl(
  dest: Destination,
  user: { lat: number; lng: number } | null,
  width: number,
  height: number
): string {
  const params = new URLSearchParams();
  // Auto-zoom to fit if both points provided; otherwise center on dest
  if (user) {
    const minLat = Math.min(dest.lat, user.lat);
    const maxLat = Math.max(dest.lat, user.lat);
    const minLng = Math.min(dest.lng, user.lng);
    const maxLng = Math.max(dest.lng, user.lng);
    const padLat = (maxLat - minLat) * 0.3 || 0.005;
    const padLng = (maxLng - minLng) * 0.3 || 0.005;
    params.set(
      'visible',
      `${minLat - padLat},${minLng - padLng}|${maxLat + padLat},${maxLng + padLng}`
    );
  } else {
    params.set('center', `${dest.lat},${dest.lng}`);
    params.set('zoom', '15');
  }
  params.set('size', `${Math.round(width)}x${Math.round(height)}`);
  params.set('scale', '2');
  params.set('maptype', 'roadmap');
  params.set(
    'markers',
    `color:red|size:small|${dest.lat},${dest.lng}`
  );
  if (user) {
    params.set(
      'markers',
      `color:blue|size:small|${user.lat},${user.lng}`
    );
    // Note: URLSearchParams.set replaces; we need append for second markers
    params.delete('markers');
  }
  // Build manually to allow duplicate markers params
  const parts: string[] = [];
  parts.push(`size=${params.get('size')}`);
  parts.push(`scale=${params.get('scale')}`);
  parts.push(`maptype=${params.get('maptype')}`);
  if (user) {
    const minLat = Math.min(dest.lat, user.lat);
    const maxLat = Math.max(dest.lat, user.lat);
    const minLng = Math.min(dest.lng, user.lng);
    const maxLng = Math.max(dest.lng, user.lng);
    const padLat = (maxLat - minLat) * 0.3 || 0.005;
    const padLng = (maxLng - minLng) * 0.3 || 0.005;
    parts.push(
      `visible=${minLat - padLat},${minLng - padLng}|${maxLat + padLat},${maxLng + padLng}`
    );
  } else {
    parts.push(`center=${dest.lat},${dest.lng}`);
    parts.push(`zoom=15`);
  }
  parts.push(`markers=color:red%7C${dest.lat},${dest.lng}`);
  if (user) {
    parts.push(`markers=color:blue%7C${user.lat},${user.lng}`);
  }
  if (STATIC_KEY) parts.push(`key=${STATIC_KEY}`);
  return `${STATIC_MAPS_BASE}?${parts.join('&')}`;
}

/** Pulsing user-location dot with heading cone, rendered as an
 *  AdvancedMarker child. Shared with MapPage and WalkingTour so all
 *  map surfaces show the same direction indicator. */
const UserDot: React.FC = () => <UserLocationMarker size={14} />;

/** Destination pin — circular red with white border. */
const DestPin: React.FC = () => (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: '50%',
      bgcolor: '#ea4335',
      border: '3px solid white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    }}
  />
);

/** Inner component that runs inside <Map>. Consumes the map instance
 *  to draw the polyline and auto-fit bounds. */
const MapInner: React.FC<{
  destination: Destination;
  user: { lat: number; lng: number } | null;
  drawRoute: boolean;
}> = ({ destination, user, drawRoute }) => {
  const map = useMap();
  const isLoaded = useApiIsLoaded();
  const { polyline, distanceM } = useWalkingRoute(
    drawRoute ? destination : null,
    drawRoute ? user : null
  );
  const polylineRef = useRef<google.maps.Polyline | null>(null);

  // Auto-fit bounds whenever destination or user changes meaningfully.
  // We don't refit on every wiggle of the user dot — useLocation
  // already throttles updates to material movement.
  useEffect(() => {
    if (!map || !isLoaded) return;
    const bounds = new google.maps.LatLngBounds();
    bounds.extend({ lat: destination.lat, lng: destination.lng });
    if (user) bounds.extend({ lat: user.lat, lng: user.lng });
    map.fitBounds(bounds, { top: 30, right: 30, bottom: 30, left: 30 });
  }, [
    map,
    isLoaded,
    destination.lat,
    destination.lng,
    user?.lat,
    user?.lng,
  ]);

  // Draw / update walking polyline. Dashed pattern via icons[] config.
  useEffect(() => {
    if (!map || !isLoaded) return;
    if (!polyline || polyline.length === 0) {
      polylineRef.current?.setMap(null);
      polylineRef.current = null;
      return;
    }
    if (!polylineRef.current) {
      polylineRef.current = new google.maps.Polyline({
        strokeOpacity: 0,
        icons: [
          {
            icon: {
              path: 'M 0,-1 0,1',
              strokeOpacity: 1,
              strokeColor: '#1a73e8',
              strokeWeight: 4,
              scale: 3,
            },
            offset: '0',
            repeat: '12px',
          },
        ],
      });
      polylineRef.current.setMap(map);
    }
    polylineRef.current.setPath(polyline);
    return () => {
      // Cleanup on unmount handled by parent unmount
    };
  }, [map, isLoaded, polyline]);

  return (
    <>
      <AdvancedMarker
        position={{ lat: destination.lat, lng: destination.lng }}
        title={destination.name}
      >
        <DestPin />
      </AdvancedMarker>
      {user && (
        <AdvancedMarker position={user}>
          <UserDot />
        </AdvancedMarker>
      )}
      {/* Distance label when route omitted (>3 km) */}
      {drawRoute === false && user && distanceM === null && (
        <DistanceLabel
          destination={destination}
          user={user}
        />
      )}
    </>
  );
};

/** Tiny "X km away" label rendered as an OverlayView at the midpoint
 *  when we're skipping the polyline (>3km destination). */
const DistanceLabel: React.FC<{
  destination: Destination;
  user: { lat: number; lng: number };
}> = ({ destination, user }) => {
  const t = useT();
  const km =
    haversineKm(user.lat, user.lng, destination.lat, destination.lng);
  return (
    <AdvancedMarker
      position={{
        lat: (user.lat + destination.lat) / 2,
        lng: (user.lng + destination.lng) / 2,
      }}
    >
      <Box
        sx={{
          bgcolor: 'rgba(0,0,0,0.75)',
          color: 'white',
          fontSize: 11,
          fontWeight: 600,
          px: 1,
          py: 0.25,
          borderRadius: 1,
          whiteSpace: 'nowrap',
        }}
      >
        {km < 10 ? km.toFixed(1) : Math.round(km)}  {t("km away")}
      </Box>
    </AdvancedMarker>
  );
};

function haversineKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export const MiniMap: React.FC<MiniMapProps> = ({
  destination,
  active = true,
  depth = 0,
  height,
  aspectRatio,
  interactive = false,
}) => {
  const { location } = useLocation();
  const user =
    location?.latitude && location?.longitude
      ? { lat: location.latitude, lng: location.longitude }
      : null;

  // Decide if we're close enough to draw the walking polyline.
  const drawRoute = useMemo(() => {
    if (!user) return false;
    const km = haversineKm(
      user.lat,
      user.lng,
      destination.lat,
      destination.lng
    );
    return km * 1000 <= WALK_LIMIT_M;
  }, [user?.lat, user?.lng, destination.lat, destination.lng]);

  const mapId = (import.meta as any).env?.VITE_GOOGLE_MAPS_MAP_ID || undefined;

  // Map surfaces are intentionally square-edged across the app —
  // rounded corners crop content (pins, route ends) and clash with
  // the photo-based card aesthetic on Discover.
  const containerSx = {
    width: '100%',
    height,
    aspectRatio,
    flexShrink: 0,
    overflow: 'hidden',
    position: 'relative' as const,
    bgcolor: '#e5e7eb',
  };

  // Depth ≥ 2: just a gradient. The user can't see this card yet.
  if (depth >= 2) {
    return (
      <Box
        sx={{
          ...containerSx,
          background: 'linear-gradient(135deg, #e0e7ef 0%, #cbd5e1 100%)',
        }}
      />
    );
  }

  // Static fallback for depth-1 or when SDK is loading. One image,
  // very lightweight. Pre-fetches network so promotion to depth 0
  // feels seamless.
  const staticImg = STATIC_KEY ? (
    <Box
      component="img"
      src={buildStaticUrl(
        destination,
        user,
        typeof height === 'number' ? height * 2 : 600,
        typeof height === 'number' ? height : 300
      )}
      alt=""
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center',
      }}
      loading="lazy"
    />
  ) : null;

  // Active mode (depth 0): live map.
  if (active && depth === 0) {
    return (
      <Box sx={containerSx}>
        {/* Static image underneath as instant-show layer; live map
            mounts on top and replaces it once tiles load. */}
        {staticImg}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            '& > div': { width: '100%', height: '100%' },
          }}
        >
          <Map
            defaultCenter={{ lat: destination.lat, lng: destination.lng }}
            defaultZoom={15}
            mapId={mapId}
            gestureHandling={interactive ? 'auto' : 'none'}
            disableDefaultUI={!interactive}
            clickableIcons={false}
            keyboardShortcuts={false}
            style={{ width: '100%', height: '100%' }}
          >
            <MapInner
              destination={destination}
              user={user}
              drawRoute={drawRoute}
            />
          </Map>
        </Box>
      </Box>
    );
  }

  // Inactive (depth 1) or no SDK: static thumbnail.
  return <Box sx={containerSx}>{staticImg}</Box>;
};
